import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import TableSkeleton from "../../../../../components/Skeletons/TableSkeleton";
import ConfirmSnackbar from "../../../../../components/Snackbar/ConfirmSnackbar";
import ConfirmDialog from "../../../../../components/ConfirmDialog/ConfirmDialog";
import { useAuditPlanFiles } from "../../hooks/useAuditPlanFile";
import { useAuditPlanAdd } from "../../hooks/useAuditPlanAdd";
import { useAuditPlanDelete } from "../../hooks/useAuditPlanDelete";
import AuditPlanEmptyState from "../../features/audit-plan/AuditPlanEmptyState";
import AuditPlanFilePreview from "../../features/audit-plan/AuditPlanFilePreview";
import AuditPlanFileList from "../../features/audit-plan/AuditPlanFileList";
import AuditPlanTabHeader from "../../features/audit-plan/AuditPlanTabHeader";

const AuditPlanTab = ({ audit, isAuditorView = false }) => {
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const { auditPlanFiles, loading, refetch } = useAuditPlanFiles(audit?.id);
  const [selectedFileUuid, setSelectedFileUuid] = useState(null);
  const [fileToDelete, setFileToDelete] = useState(null);

  const selectedFile = useMemo(
    () =>
      auditPlanFiles.find((f) => f.fileUuid === selectedFileUuid) ||
      auditPlanFiles[0] ||
      null,
    [auditPlanFiles, selectedFileUuid]
  );

  useEffect(() => {
    if (auditPlanFiles.length > 0 && !selectedFileUuid) {
      setSelectedFileUuid(auditPlanFiles[0].fileUuid);
    }
    if (
      selectedFileUuid &&
      !auditPlanFiles.some((f) => f.fileUuid === selectedFileUuid)
    ) {
      setSelectedFileUuid(auditPlanFiles[0]?.fileUuid ?? null);
    }
  }, [auditPlanFiles, selectedFileUuid]);

  const { adding, addFile } = useAuditPlanAdd(audit?.id, {
    onSuccess: async () => {
      await refetch();
      setSnackbarInfo({
        message: "Audit plan file added successfully",
        vertical: "top",
        horizontal: "right",
      });
    },
    onError: (message) => {
      setSnackbarInfo({
        message: message || "Failed to add audit plan",
        vertical: "top",
        horizontal: "right",
      });
    },
  });

  const { deleting, deletePlanFile } = useAuditPlanDelete({
    onSuccess: async () => {
      await refetch();
      setFileToDelete(null);
      setSnackbarInfo({
        message: "Audit plan file deleted",
        vertical: "top",
        horizontal: "right",
      });
    },
    onError: (message) => {
      setSnackbarInfo({
        message: message || "Failed to delete file",
        vertical: "top",
        horizontal: "right",
      });
    },
  });

  const hasFiles = !loading && auditPlanFiles.length > 0;

  return (
    <Box>
      <Card
        sx={{
          minHeight: "600px",
          borderRadius: "12px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #E8EAED",
          overflow: "hidden",
        }}
        variant="outlined"
      >
        <CardHeader
          title={
            hasFiles ? (
              <AuditPlanTabHeader
                fileCount={auditPlanFiles.length}
                isAuditorView={isAuditorView}
                adding={adding}
                onAddFile={addFile}
              />
            ) : (
              "Audit Plan"
            )
          }
          sx={{
            borderBottom: "1px solid #ddd",
            background: "#F7F7FC",
            ".MuiCardHeader-title": {
              fontSize: hasFiles ? "inherit" : "18px",
              fontWeight: hasFiles ? "inherit" : "bold",
              width: "100%",
            },
            padding: "14px",
          }}
        />

        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <TableSkeleton />
          ) : !hasFiles ? (
            <AuditPlanEmptyState
              onUploadFile={addFile}
              uploading={adding}
              isAuditorView={isAuditorView}
            />
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "300px 1fr" },
                alignItems: "stretch",
                gap: 2,
                p: 2,
                bgcolor: "#F7F8FA",
              }}
            >
              <AuditPlanFileList
                files={auditPlanFiles}
                selectedFileUuid={selectedFile?.fileUuid}
                onSelect={(f) => setSelectedFileUuid(f.fileUuid)}
                onDelete={(f) => setFileToDelete(f)}
                isAuditorView={isAuditorView}
                busy={deleting || adding}
              />
              {selectedFile ? (
                <AuditPlanFilePreview
                  key={selectedFile.fileUuid || selectedFile.fileId}
                  file={selectedFile}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 500,
                    bgcolor: "#FFFFFF",
                    borderRadius: "8px",
                    border: "1px solid #E5E7EB",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Select a file to preview
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!fileToDelete}
        onClose={() => (!deleting ? setFileToDelete(null) : undefined)}
        onConfirm={() => deletePlanFile(fileToDelete?.fileUuid)}
        heading="Delete audit plan file"
        content={
          <>
            Are you sure you want to remove{" "}
            <strong>{fileToDelete?.maskName || "this file"}</strong> from this
            audit?
          </>
        }
        buttonText="Delete"
        loading={deleting}
        note="This action cannot be undone. The file will be removed from this audit's plan."
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default AuditPlanTab;
